import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { isInvestmentAccountType, type BankAccountType } from '@fint/validation';
import { api, ApiError } from '@/lib/api';
import { formatDate } from '@/lib/dates';
import { Button, Input, Money, SelectField } from '@/features/common/ui';

/**
 * Investment accounts (FINT-2): fixed and recurring deposits, funds, chits.
 *
 * They are accounts so that money moving into one is a TRANSFER - out of the
 * bank, into the FD - which no income or expense figure reads. The server
 * keeps them out of cash, runway and the forecast; this file is the screens'
 * half: the type picker, the terms, and saying "this line went to the FD".
 */

export type AccountType = BankAccountType;

export const TYPE_LABEL: Record<AccountType, string> = {
  bank: 'Bank',
  credit_card: 'Credit card',
  cash: 'Cash',
  other: 'Other',
  fixed_deposit: 'Fixed deposit',
  recurring_deposit: 'Recurring deposit',
  mutual_fund: 'Mutual fund / SIP',
  chit: 'Chit',
  other_investment: 'Other investment',
};

export const isInvestment = (type: string): boolean => isInvestmentAccountType(type);

/** The terms an investment account carries. Strings, as typed; '' is none. */
export interface TermsForm {
  principalAmount: string;
  interestRate: string;
  startDate: string;
  maturityDate: string;
  autoRenew: boolean;
}

export const emptyTerms = (): TermsForm => ({ principalAmount: '', interestRate: '', startDate: '', maturityDate: '', autoRenew: false });

export function termsFrom(account: {
  principalAmount?: string | null;
  interestRate?: string | null;
  startDate?: string | null;
  maturityDate?: string | null;
  autoRenew?: boolean;
}): TermsForm {
  return {
    principalAmount: account.principalAmount ?? '',
    interestRate: account.interestRate ?? '',
    startDate: account.startDate ?? '',
    maturityDate: account.maturityDate ?? '',
    autoRenew: account.autoRenew ?? false,
  };
}

/**
 * The body keys for the terms. On a non-investment type nothing is sent (the
 * server refuses terms there, and clears them when the type changes away).
 * A blank field is sent as null so an edit can clear it.
 */
export function termsBody(type: string, terms: TermsForm): Record<string, unknown> {
  if (!isInvestment(type)) return {};
  return {
    principalAmount: terms.principalAmount.trim() || null,
    interestRate: terms.interestRate.trim() || null,
    startDate: terms.startDate || null,
    maturityDate: terms.maturityDate || null,
    autoRenew: terms.autoRenew,
  };
}

export function AccountTypeSelect({
  id,
  value,
  onChange,
}: {
  id: string;
  value: AccountType;
  onChange: (type: AccountType) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium">
        Type
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value as AccountType)}
        className="h-9 rounded-md border border-input bg-background px-3 text-sm shadow-sm"
      >
        <optgroup label="Money you spend from">
          <option value="bank">Bank</option>
          <option value="credit_card">Credit card</option>
          <option value="cash">Cash</option>
          <option value="other">Other</option>
        </optgroup>
        <optgroup label="Savings and investments">
          <option value="fixed_deposit">Fixed deposit</option>
          <option value="recurring_deposit">Recurring deposit</option>
          <option value="mutual_fund">Mutual fund / SIP</option>
          <option value="chit">Chit</option>
          <option value="other_investment">Other investment</option>
        </optgroup>
      </select>
      {isInvestment(value) ? (
        <p className="text-xs text-muted-foreground">
          Not counted as cash. Money moved in from a bank account is a transfer, not an expense.
        </p>
      ) : null}
    </div>
  );
}

/** Principal, rate, dates, auto-renew - shown only for an investment type. */
export function InvestmentTermsFields({
  type,
  terms,
  onChange,
  error,
}: {
  type: AccountType;
  terms: TermsForm;
  onChange: (terms: TermsForm) => void;
  error?: ApiError;
}) {
  if (!isInvestment(type)) return null;
  const set = <K extends keyof TermsForm>(key: K, value: TermsForm[K]) => onChange({ ...terms, [key]: value });
  const hasRate = type !== 'mutual_fund';
  return (
    <fieldset className="space-y-3 rounded-md border border-border p-3" data-testid="investment-terms">
      <legend className="px-1 text-sm font-medium">Terms</legend>
      <Input
        label="Principal"
        inputMode="decimal"
        value={terms.principalAmount}
        onChange={(e) => set('principalAmount', e.target.value)}
        error={error?.fieldError('principalAmount')}
        hint="What was put in at the start. The balance still comes from the transfers in and out."
      />
      {hasRate ? (
        <Input
          label="Interest rate (% a year)"
          inputMode="decimal"
          value={terms.interestRate}
          onChange={(e) => set('interestRate', e.target.value)}
          error={error?.fieldError('interestRate')}
        />
      ) : null}
      <div className="grid gap-3 sm:grid-cols-2">
        <Input
          label="Start date"
          type="date"
          value={terms.startDate}
          onChange={(e) => set('startDate', e.target.value)}
          error={error?.fieldError('startDate')}
        />
        <Input
          label="Maturity date"
          type="date"
          value={terms.maturityDate}
          onChange={(e) => set('maturityDate', e.target.value)}
          error={error?.fieldError('maturityDate')}
        />
      </div>
      <label className="flex items-start gap-2 text-sm">
        <input type="checkbox" checked={terms.autoRenew} onChange={(e) => set('autoRenew', e.target.checked)} className="mt-0.5" />
        <span>Renews on maturity</span>
      </label>
    </fieldset>
  );
}

/** "7.1% · matures 17 Oct 2027 · renews" under an investment's name on its card. */
export function TermsLine({
  account,
}: {
  account: { interestRate?: string | null; maturityDate?: string | null; autoRenew?: boolean; principalAmount?: string | null; currency: string };
}) {
  const parts: React.ReactNode[] = [];
  if (account.principalAmount) {
    parts.push(
      <span key="p">
        <Money value={account.principalAmount} currency={account.currency} /> in
      </span>,
    );
  }
  if (account.interestRate) parts.push(<span key="r">{Number(account.interestRate)}%</span>);
  if (account.maturityDate) parts.push(<span key="m">matures {formatDate(account.maturityDate)}</span>);
  if (account.autoRenew) parts.push(<span key="a">renews</span>);
  if (!parts.length) return null;
  return (
    <p className="mt-1 flex flex-wrap gap-x-1 text-xs text-muted-foreground" data-testid="investment-terms-line">
      {parts.map((part, i) => (
        <span key={i}>
          {i > 0 ? '· ' : ''}
          {part}
        </span>
      ))}
    </p>
  );
}

interface AccountOption {
  id: string;
  name: string;
  type: AccountType;
  currency: string;
}

/**
 * "This line was money moving to (or from) one of my accounts."
 *
 * The bank line becomes one leg of a transfer and the other account gets the
 * other leg - or, when that account's statement already has it waiting, the
 * two are paired. No income or expense figure moves. This is the door the
 * 17 Oct FD needed: Hdfc company → the FD, and both balances right.
 */
export function TransferChoice({
  transaction,
  accounts,
  onSaved,
}: {
  transaction: { id: string; accountId: string; direction: 'deposit' | 'withdrawal'; currency: string };
  accounts: AccountOption[];
  onSaved: () => void;
}) {
  const others = accounts.filter((a) => a.id !== transaction.accountId && a.currency === transaction.currency);
  // An investment first on a withdrawal - that is the usual reason to be here.
  const ordered = [...others].sort((a, b) => Number(isInvestment(b.type)) - Number(isInvestment(a.type)));
  const [counterpart, setCounterpart] = useState('');
  const save = useMutation({
    mutationFn: () =>
      api.post(`/banking/transactions/${transaction.id}/categorise`, { linkType: 'transfer', counterpartAccountId: counterpart }),
    onSuccess: onSaved,
  });
  const error = save.error instanceof ApiError ? save.error : undefined;
  const out = transaction.direction === 'withdrawal';

  if (!others.length) {
    return (
      <p className="rounded-md border border-dashed border-border p-3 text-xs text-muted-foreground">
        {out
          ? 'Moved this into a fixed deposit, a fund or another bank? Add that account first, then come back to say this went there - it is a transfer, not an expense.'
          : 'An FD paying out, or money from another bank? Add that account first, then come back to say this came from it.'}
      </p>
    );
  }

  return (
    <div className="space-y-2 rounded-md border border-dashed border-border p-3 text-sm" data-testid="transfer-choice">
      <p className="text-xs text-muted-foreground">
        {out
          ? 'Money moved to one of your own accounts - a fixed deposit, a SIP, a chit, another bank - is a transfer: not an expense, and the other account gets it.'
          : 'Money from one of your own accounts - an FD paying out, a fund redeemed, another bank - is a transfer: not income.'}
      </p>
      <SelectField id={`transfer-${transaction.id}`} label={out ? 'Moved to' : 'Came from'} value={counterpart} onChange={setCounterpart}>
        <option value="">Pick the account</option>
        {ordered.map((a) => (
          <option key={a.id} value={a.id}>
            {a.name} · {TYPE_LABEL[a.type]}
          </option>
        ))}
      </SelectField>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={!counterpart}
        loading={save.isPending}
        onClick={() => save.mutate()}
        data-testid="transfer-save"
      >
        {out ? 'This was a transfer to that account' : 'This was a transfer from that account'}
      </Button>
      {error ? (
        <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error.message}
        </p>
      ) : null}
    </div>
  );
}
